import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { CSSTransitionGroup } from 'src/utils/react-transition-group-compat';
import css from '../../utils/css';
import { StickyContainer } from '../sticky';
import classnames from 'classnames';
import FocusTrap from '../focus-trap';
import LucideIcon from '../lucide-icon';
import focusManager from '../../utils/focus-manager';
import LoadingOverlay from '../loading-overlay';
import { Row, Col } from 'reactstrap';
import { FormattedMessage } from 'react-intl';
import { X } from 'lucide-react';
import {
  addModalOpenClass,
  removeModalOpenClass,
} from 'src/utils/modal-open-manager';

const transitionTimeouts = {
  appear: 250,
  enter: 250,
  leave: 250,
};

let baseClasses = css`
  .container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 11000;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    overflow-y: auto;
  }

  .overlay {
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);

    &.dialog-transition-enter,
    &.dialog-transition-appear {
      opacity: 0;
    }

    &.dialog-transition-enter.dialog-transition-enter-active,
    &.dialog-transition-appear.dialog-transition-appear-active {
      opacity: 1;
      transition: all ${transitionTimeouts.enter}ms ease;
    }

    &.dialog-transition-leave {
      opacity: 1;
    }

    &.dialog-transition-leave.dialog-transition-leave-active {
      opacity: 0;
      transition: all ${transitionTimeouts.leave}ms ease;
    }
  }

  .dialog {
    position: relative;
    box-shadow: 0 14px 45px rgba(0, 0, 0, 0.25), 0 10px 18px rgba(0, 0, 0, 0.22);
    display: flex;
    flex-direction: column;
    outline: none;
    height: auto !important;
    border-radius: 0.3rem;
    &.dialog-transition-enter,
    &.dialog-transition-appear {
      opacity: 0;
      transform: translate(0, -100px);
    }

    &.dialog-transition-enter.dialog-transition-enter-active,
    &.dialog-transition-appear.dialog-transition-appear-active {
      opacity: 1;
      transition: all ${transitionTimeouts.enter}ms ease;
      transform: translate(0, 0);
    }

    &.dialog-transition-leave {
      opacity: 1;
      transform: translate(0, 0);
    }

    &.dialog-transition-leave.dialog-transition-leave-active {
      opacity: 0;
      transition: all ${transitionTimeouts.leave}ms ease;
      transform: translate(0, 100px);
    }
  }

  .header {
    padding: 10px 15px;
    font-size: 15px;
    text-transform: uppercase;
    display: flex;
    flex-shrink: 0;
    flex-grow: 0;
  }

  .title {
    color: #000;
    font-weight: bold;
    flex: 1 1 auto;
  }

  .titleLink {
    display: flex;
    justify-content: space-between;
    flex: 1 1 auto;
    align-items: center;
    padding-right: 35px;
  }

  .close {
    cursor: pointer;
  }

  .maximize {
    margin-right: 10px;
    color: #747b84;
    cursor: pointer;
  }

  .content {
    position: relative;
    flex-grow: 1;
    overflow: auto;
    overflow-x: auto;

    &.padContent {
      padding: 0px 15px 0px 15px;
    }
  }

  .footer {
    padding: 5px 15px;
    flex-shrink: 0;
    flex-grow: 0;

    .content {
      flex: 1 1 auto;
    }

    .buttons {
      flex: 0 0 auto;
      white-space: nowrap;
    }

    .col-md-12 .pull-right > div[class='']:first-child {
      display: flex;
      flex-direction: row-reverse;
      justify-content: space-between;
    }
  }
`;

function tratarUnidade(valor, width = false) {
  if (valor === undefined || valor === null) {
    return undefined;
  }

  if (typeof valor === `string`) {
    if (valor.indexOf('%') !== -1) {
      const unidadeMedida = valor.substring(0, valor.length - 1);
      return width === true ? `${unidadeMedida}vw` : valor;
    } else if (valor.indexOf('px') !== -1) {
      return valor;
    } else if (valor.indexOf('vw') !== -1) {
      return valor;
    } else if (valor.indexOf('rem') !== -1) {
      return valor;
    }
  }

  return `${valor}px`;
}

function renderTitle(title, ignoreResourceTitle = false) {
  const isString = typeof title === 'string';
  if (isString) {
    if (ignoreResourceTitle) return title;

    return <FormattedMessage id={title} />;
  }

  return title;
}

const Dialog = forwardRef(function Dialog(
  {
    id,
    className,
    showClose = true,
    width,
    height,
    minWidth,
    minHeight,
    maxHeight,
    maxWidth,
    padContent,
    mouseDownClose = false,
    title,
    ignoreResourceTitle = false,
    styleTitle = {},
    styleHeader = {},
    customTitle,
    link,
    respeitarAltura = false,
    background,
    customClose,
    showHeader = true,
    textCapitalizeTitle = true,
    customHandleCloseFunction,
    titleCapitalizeNone,
    customButton,
    removeTitleMargin = false,
    zIndex,
    remove,
    onClose,
    onRequestClose,
    showActionsEnd,
    actions,
    footer,
    styleFooter,
    renderButtonActions,
    botoesCustomLeft,
    style,
    index,
    handleAssistenteVirtualClick,
    children,
    isLoading,
    allowMaximize = true,
    maximized,
    onMaximizeChange,
  },
  ref
) {
  const [isOpen, setIsOpen] = useState(true);
  const [maximizedInterno, setMaximizedInterno] = useState(false);
  const isMaximized = maximized != null ? maximized : maximizedInterno;

  const toggleMaximize = useCallback(() => {
    const novoValor = !isMaximized;
    if (maximized == null) setMaximizedInterno(novoValor);
    onMaximizeChange && onMaximizeChange(novoValor);
  }, [isMaximized, maximized, onMaximizeChange]);

  const close = useCallback(() => {
    setIsOpen(false);

    setTimeout(() => {
      remove && remove();
      onClose && onClose();
    }, transitionTimeouts.leave);
  }, [onClose, remove]);

  useImperativeHandle(
    ref,
    () => ({
      close,
    }),
    [close]
  );

  useEffect(() => {
    addModalOpenClass();

    const timeout = setTimeout(() => {
      focusManager.ensureFocusIsValid();
    }, transitionTimeouts.appear);

    return () => {
      clearTimeout(timeout);
      removeModalOpenClass();
    };
  }, []);

  const handleCloseClick = useCallback(() => {
    if (onRequestClose) {
      onRequestClose();
    } else {
      close();
    }
  }, [close, onRequestClose]);

  const renderActions = (classes) => {
    if (showActionsEnd)
      return (
        (actions || footer) && (
          <div className={classes.footer} style={styleFooter}>
            <Row>
              <Col md={footer ? 7 : 12}>
                <div className="pull-right">
                  {actions &&
                    actions.length &&
                    actions.map((action, actionIndex) =>
                      React.cloneElement(action, {
                        key: actionIndex,
                      })
                    )}
                </div>
              </Col>
              {footer && <Col md={5}>{footer}</Col>}
            </Row>
          </div>
        )
      );

    return (
      (actions || footer) && (
        <div className={classes.footer} style={styleFooter}>
          <Row>
            {footer && <Col md={5}>{footer}</Col>}
            <Col md={footer ? 7 : 12}>
              <div
                className="pull-right"
                style={
                  renderButtonActions && botoesCustomLeft
                    ? { width: '100%' }
                    : {}
                }
              >
                {actions &&
                  actions.length &&
                  actions.map((action, actionIndex) =>
                    React.cloneElement(action, {
                      key: actionIndex,
                    })
                  )}
              </div>
            </Col>
          </Row>
        </div>
      )
    );
  };

  const isLinkFunction = typeof link === 'function';
  let classes = isMaximized
    ? css`
        .dialog {
          width: 100vw;
          height: 100% !important;
          min-width: 0px;
          min-height: 0px;
          max-height: 100% !important;
          max-width: 100%;
          border-radius: 0;
          overflow: hidden;
        }
      `.extend(baseClasses)
    : css`
        .dialog {
          width: ${tratarUnidade(width, true) || 'auto'};
          height: ${tratarUnidade(height) || 'auto'}${respeitarAltura ? ' !important' : ''};
          min-width: ${tratarUnidade(minWidth) || '0px'};
          min-height: ${tratarUnidade(minHeight) || '0px'};
          max-height: ${tratarUnidade(maxHeight) || '100%'}${respeitarAltura ? ' !important' : ''};
          max-width: ${tratarUnidade(maxWidth) || '100%'};
          ${respeitarAltura ? 'overflow: hidden;' : ''}
        }
      `.extend(baseClasses);

  if (respeitarAltura || isMaximized) {
    classes = css`
      .content {
        min-height: 0;
      }
    `.extend(classes);
  }

  const mergedStyle = {
    background: background || '#fff',
    ...style,
    ...(isMaximized ? { borderRadius: 0 } : {}),
  };

  // Detecta se existe um elemento em fullscreen e renderiza o Dialog dentro dele
  const fullscreenElement =
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.msFullscreenElement;

  const dialogContent = (
    <CSSTransitionGroup
      id={id}
      component="div"
      className={classnames(classes.container, className, 'dialog')}
      style={zIndex ? { zIndex } : undefined}
      transitionName="dialog-transition"
      transitionAppear={true}
      transitionAppearTimeout={transitionTimeouts.appear}
      transitionEnterTimeout={transitionTimeouts.enter}
      transitionLeaveTimeout={transitionTimeouts.leave}
    >
      {isOpen && [
        <div
          key="overlay"
          className={classes.overlay}
          onMouseDown={mouseDownClose ? handleCloseClick : undefined}
        ></div>,
        <FocusTrap
          key="dialog"
          className={classes.dialog}
          style={mergedStyle}
          trap={index + 1}
          tabIndex="0"
        >
          {showHeader && (
            <div
              className={classnames(classes.header, 'dialog-header')}
              style={styleHeader}
            >
              <div className={classes.titleLink}>
                {title && (
                  <div
                    tabIndex={handleAssistenteVirtualClick && '0'}
                    className={`font-weight-bolder ${
                      styleTitle?.color ? '' : 'text-primary'
                    } ${removeTitleMargin ? '' : 'mb-2'} ${
                      textCapitalizeTitle
                        ? 'text-capitalize'
                        : titleCapitalizeNone
                        ? 'text-transform-none'
                        : ''
                    }`}
                    style={styleTitle ? styleTitle : {}}
                  >
                    {renderTitle(title, ignoreResourceTitle)}
                  </div>
                )}
                <div style={{ textTransform: 'none' }}>
                  {customTitle && customTitle}
                </div>
                {link && isLinkFunction && link()}
              </div>
              {allowMaximize && (
                <LucideIcon
                  className={classes.maximize}
                  icon={isMaximized ? 'minimize-2' : 'maximize-2'}
                  size={16}
                  onClick={toggleMaximize}
                />
              )}
              {showClose &&
                (customClose ? (
                  <>
                    {customClose(handleCloseClick)}
                    {customButton && customButton()}
                  </>
                ) : (
                  <X
                    className={classes.close}
                    size={16}
                    onClick={
                      customHandleCloseFunction
                        ? customHandleCloseFunction
                        : handleCloseClick
                    }
                  />
                ))}
            </div>
          )}
          <StickyContainer
            id="dialog-sticky-container"
            className={classnames(classes.content, { padContent })}
          >
            {children}
            {isLoading && <LoadingOverlay />}
          </StickyContainer>
          {renderActions(classes)}
        </FocusTrap>,
      ]}
    </CSSTransitionGroup>
  );

  // Se houver um elemento fullscreen, renderiza o Dialog dentro dele usando portal
  if (fullscreenElement) {
    return createPortal(dialogContent, fullscreenElement);
  }

  return dialogContent;
});

Dialog.displayName = 'Dialog';

export default Dialog;
